import { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

export const badgeVariants = cva(
  'inline-flex items-center gap-1 whitespace-nowrap rounded-full font-medium [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        neutral: 'bg-surface-sunken text-fg-muted ring-1 ring-inset ring-border',
        primary: 'bg-primary-subtle text-primary-subtle-fg ring-1 ring-inset ring-primary-border',
        accent: 'bg-accent-subtle text-accent-subtle-fg ring-1 ring-inset ring-accent-border',
        success: 'bg-success-subtle text-success-subtle-fg ring-1 ring-inset ring-success-border',
        warning: 'bg-warning-subtle text-warning-subtle-fg ring-1 ring-inset ring-warning-border',
        danger: 'bg-danger-subtle text-danger-subtle-fg ring-1 ring-inset ring-danger-border',
        info: 'bg-info-subtle text-info-subtle-fg ring-1 ring-inset ring-info-border',
        outline: 'bg-transparent text-fg-muted ring-1 ring-inset ring-border-strong',
      },
      size: {
        sm: 'px-1.5 py-0.5 text-2xs [&_svg]:size-3',
        md: 'px-2 py-0.5 text-xs [&_svg]:size-3.5',
        lg: 'px-2.5 py-1 text-sm [&_svg]:size-4',
      },
    },
    defaultVariants: { variant: 'neutral', size: 'md' },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { className, variant, size, ...props },
  ref,
) {
  return <span ref={ref} className={cn(badgeVariants({ variant, size }), className)} {...props} />;
});

/** Contador compacto para pestañas y navegación (`12`, `99+`). */
export function CountBadge({
  value,
  className,
  'aria-label': ariaLabel,
}: {
  value: number;
  className?: string;
  'aria-label'?: string;
}) {
  if (!value) return null;
  return (
    <span
      className={cn(
        'tabular inline-flex min-w-[18px] items-center justify-center rounded-full bg-danger px-1.5 py-0.5 text-2xs font-semibold text-danger-fg',
        className,
      )}
      // El contador cambia solo (mensajes nuevos): `polite` lo anuncia sin
      // interrumpir lo que la persona esté haciendo.
      aria-live="polite"
      aria-label={ariaLabel}
    >
      {value > 99 ? '99+' : value}
    </span>
  );
}
